/**
 * BulkMediaDownloadButton — One-click ZIP download of every photo/video on an
 * inspection. Sits next to the MediaQualityToggle in the LiveProgressModal
 * header. Files are organized in a folder named after the car number.
 *
 * Architecture (Apr 2026):
 *   • Backend endpoint `GET /api/inspections/{id}/media-bundle` returns a
 *     manifest: { folder_name, items: [{ url, media_type, category_name }] }.
 *   • Browser fetches each CDN URL DIRECTLY (Firebase / CDN traffic, NOT
 *     Railway egress — important because High-tier inspections can be 200+ MB).
 *   • JSZip stitches them into a single .zip and FileSaver triggers download.
 *   • Live per-file progress in the toast — user sees X/Y files downloaded.
 *
 * Inside the ZIP:
 *   {car_number}/
 *     01_engine_front.jpg
 *     02_engine_left.jpg
 *     ...
 *     videos/
 *       01_walkaround.mp4
 *
 * The media quality of each downloaded file is whatever the mechanic uploaded
 * with at capture time — driven by the per-inspection `media_quality_tier`
 * (Low / Medium / High). This component does NOT re-compress.
 */
import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

function safeFilenameFromUrl(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').pop() || 'file';
    // Drop signed-URL query params; preserve extension.
    return decodeURIComponent(last).replace(/[^A-Za-z0-9._-]/g, '_');
  } catch {
    return 'file';
  }
}

function extFromUrl(url, mediaType) {
  const m = (url || '').toLowerCase().match(/\.([a-z0-9]{2,5})(\?|$)/);
  if (m) return m[1];
  return mediaType === 'video' ? 'mp4' : 'jpg';
}

export default function BulkMediaDownloadButton({ inspectionId }) {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const handleDownload = async () => {
    if (!inspectionId || downloading) return;
    setDownloading(true);
    setProgress({ done: 0, total: 0 });

    let toastId = null;
    try {
      // 1. Fetch manifest from backend
      toastId = toast.loading('Preparing download…');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await fetch(
        `${apiUrl}/api/inspections/${inspectionId}/media-bundle`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        toast.error('Could not load media list', {
          id: toastId, description: body.detail || `HTTP ${r.status}`,
        });
        return;
      }
      const manifest = await r.json();
      const items = manifest.items || [];
      if (items.length === 0) {
        toast.info('No media uploaded yet for this inspection', { id: toastId });
        return;
      }
      const folder = manifest.folder_name || 'inspection';

      // 2. Build the zip — one file at a time so we can show progress and
      //    keep memory bounded on large inspections (200+ MB at High tier).
      const zip = new JSZip();
      const root = zip.folder(folder);
      const videosDir = root.folder('videos');

      let done = 0;
      const total = items.length;
      setProgress({ done, total });
      toast.loading(`Downloading 0 / ${total}…`, { id: toastId });

      // Track filename collisions (multiple photos for the same question)
      const usedNames = new Set();
      const claim = (base) => {
        let name = base;
        let i = 1;
        while (usedNames.has(name)) {
          const dot = base.lastIndexOf('.');
          const stem = dot >= 0 ? base.slice(0, dot) : base;
          const ext = dot >= 0 ? base.slice(dot) : '';
          name = `${stem}_${i}${ext}`;
          i += 1;
        }
        usedNames.add(name);
        return name;
      };

      const failures = [];
      // Helper: parse a `data:image/jpeg;base64,...` URI into a Blob without
      // hitting the network — fetch() on data URIs works in modern browsers
      // but avoiding the round-trip is faster for large inspections (44+ photos).
      const dataUriToBlob = (uri) => {
        const m = uri.match(/^data:([^;,]+)(?:;base64)?,(.*)$/);
        if (!m) return null;
        const mime = m[1] || 'application/octet-stream';
        const isB64 = uri.includes(';base64,');
        const body = m[2];
        if (isB64) {
          const binary = atob(body);
          const len = binary.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
          return new Blob([bytes], { type: mime });
        }
        return new Blob([decodeURIComponent(body)], { type: mime });
      };

      // Sequential — keeps memory low and avoids hammering Firebase with
      // 50 parallel requests from the same browser tab.
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        try {
          let blob;
          if (typeof it.url === 'string' && it.url.startsWith('data:')) {
            // Inline data URI (older inspections store base64 directly in Mongo)
            blob = dataUriToBlob(it.url);
            if (!blob) {
              failures.push({ url: it.url.slice(0, 40), reason: 'bad data URI' });
            }
          } else {
            const resp = await fetch(it.url, { credentials: 'omit' });
            if (!resp.ok) {
              failures.push({ url: it.url, reason: `HTTP ${resp.status}` });
            } else {
              blob = await resp.blob();
            }
          }
          if (blob) {
            // Pick extension from blob mime when the source is a data URI
            // (data URIs don't carry a filename), otherwise from the URL.
            let ext;
            if (typeof it.url === 'string' && it.url.startsWith('data:')) {
              const mime = (blob.type || '').toLowerCase();
              if (mime.includes('mp4') || mime.includes('quicktime')) ext = 'mp4';
              else if (mime.includes('png')) ext = 'png';
              else if (mime.includes('webp')) ext = 'webp';
              else ext = 'jpg';
            } else {
              ext = extFromUrl(it.url, it.media_type);
            }
            const cat = (it.category_name || 'misc')
              .replace(/[^A-Za-z0-9._-]/g, '_')
              .slice(0, 40);
            const idx = String(i + 1).padStart(3, '0');
            const stem = it.url.startsWith('data:')
              ? (it.question_id || 'photo').slice(0, 12)
              : safeFilenameFromUrl(it.url).slice(0, 40);
            const filename = claim(`${idx}_${cat}_${stem}.${ext}`);
            const target = it.media_type === 'video' ? videosDir : root;
            target.file(filename, blob);
          }
        } catch (err) {
          failures.push({ url: (it.url || '').slice(0, 40), reason: err.message });
        }
        done += 1;
        setProgress({ done, total });
        // Throttle toast updates so we don't re-render 100 times.
        if (done === total || done % Math.max(1, Math.floor(total / 20)) === 0) {
          toast.loading(`Downloading ${done} / ${total}…`, { id: toastId });
        }
      }

      if (failures.length === total) {
        toast.error('Could not fetch any media', {
          id: toastId,
          description: 'Check your network connection and try again.',
        });
        return;
      }

      // 3. Generate the zip and trigger save
      toast.loading(`Building ZIP (${total} files)…`, { id: toastId });
      const zipBlob = await zip.generateAsync(
        { type: 'blob', compression: 'STORE' },  // STORE: don't re-compress JPEG/MP4
        (meta) => {
          // ZIP-build progress (separate from fetch progress above)
          if (meta.percent && Math.round(meta.percent) % 10 === 0) {
            toast.loading(`Building ZIP (${Math.round(meta.percent)}%)…`, { id: toastId });
          }
        },
      );
      saveAs(zipBlob, `${folder}.zip`);

      const failedCount = failures.length;
      if (failedCount > 0) {
        toast.warning(`Downloaded ${total - failedCount} of ${total} files`, {
          id: toastId,
          description: `${failedCount} file(s) failed to fetch. Saved what we could.`,
          duration: 8000,
        });
      } else {
        toast.success(`Downloaded ${total} files`, {
          id: toastId,
          description: `Saved as ${folder}.zip`,
          duration: 6000,
        });
      }
    } catch (e) {
      toast.error('Download failed', {
        id: toastId,
        description: e.message || 'Unknown error',
      });
    } finally {
      setDownloading(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      data-testid="bulk-media-download-button"
      title="Download all photos & videos for this inspection as a ZIP organized by car number"
      className={[
        'flex items-center gap-1.5 bg-slate-900/60 ring-1 ring-white/20 rounded-lg px-2.5 py-1',
        'text-[11px] font-bold tracking-wide transition-all duration-150',
        downloading
          ? 'text-blue-100 cursor-wait'
          : 'text-white hover:bg-slate-700/80 hover:ring-white/40 cursor-pointer shadow-inner',
      ].join(' ')}
    >
      {downloading ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>
            {progress.total > 0
              ? `${progress.done}/${progress.total}`
              : 'Preparing…'}
          </span>
        </>
      ) : (
        <>
          <Download className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">DOWNLOAD ALL</span>
          <span className="sm:hidden">ZIP</span>
        </>
      )}
    </button>
  );
}
